import { useState } from "react";
import { useTranslation } from "react-i18next";
import { AppShell } from "../components/AppShell";
import { FeedbackControl } from "../components/FeedbackControl";
import { Card, ErrorBox, ProvenanceBadge, Skeleton } from "../components/ui";
import {
  draftReportAi, draftReportRule, runReport, runSavedReport, saveReport, updateSavedReport, useSavedReports, useWhoAmI,
  type ReportDraft, type ReportRun, type ReportSpec,
} from "../lib/api";
import { fmtNumber } from "../lib/format";

const EXAMPLES = ["e1", "e2", "e3", "e4"] as const;

/** Reports in plain language (Client AI-4): instruction → validated spec (rule or AI) → review → permission-aware run → save / share. */
export default function Reports() {
  const { t, i18n } = useTranslation();
  const lng = i18n.language;
  const whoami = useWhoAmI();
  const saved = useSavedReports();
  const [instruction, setInstruction] = useState("");
  const [draft, setDraft] = useState<ReportDraft | null>(null);
  const [run, setRun] = useState<ReportRun | null>(null);
  const [busy, setBusy] = useState<"rule" | "ai" | "run" | "save" | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [title, setTitle] = useState("");
  const [shared, setShared] = useState(false);
  const [savedName, setSavedName] = useState<string | null>(null);

  const doDraft = async (mode: "rule" | "ai") => {
    if (!instruction.trim()) return;
    setBusy(mode); setError(null); setRun(null); setSavedName(null);
    try {
      const d = mode === "ai" ? await draftReportAi(instruction, lng) : await draftReportRule(instruction, lng);
      setDraft(d);
      if (d.spec) setTitle(d.spec.title || instruction.slice(0, 80));
    } catch (e) { setError(e instanceof Error ? e.message : String(e)); } finally { setBusy(null); }
  };
  const doRun = async (spec: ReportSpec) => {
    setBusy("run"); setError(null);
    try { setRun(await runReport(spec, lng)); } catch (e) { setError(e instanceof Error ? e.message : String(e)); } finally { setBusy(null); }
  };
  const doSave = async () => {
    if (!draft?.spec) return;
    setBusy("save"); setError(null);
    try {
      const r = await saveReport({ title: title || instruction.slice(0, 80), spec: draft.spec, instruction, source: draft.source, language: lng.slice(0, 2), is_shared: shared, model: draft.model ?? undefined, interaction_id: draft.interaction_id ?? undefined });
      setSavedName(r.name); void saved.refetch();
    } catch (e) { setError(e instanceof Error ? e.message : String(e)); } finally { setBusy(null); }
  };
  const openSaved = async (name: string, spec: ReportSpec) => {
    setBusy("run"); setError(null); setSavedName(null);
    setDraft({ interaction_id: null, source: "human", model: null, spec, explanation: null, errors: [], notes: [], error: null });
    setTitle(spec.title ?? "");
    try { setRun(await runSavedReport(name, lng)); } catch (e) { setError(e instanceof Error ? e.message : String(e)); } finally { setBusy(null); }
  };
  const canAct = !!whoami.data;
  const spec = draft?.spec ?? null;
  const setChart = (chart: ReportSpec["chart"]) => { if (spec) setDraft({ ...draft!, spec: { ...spec, chart } }); };

  return (
    <AppShell subtitle={t("rpt.title")}>
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div>
          <h1 className="font-heading text-2xl font-semibold text-brand-primary">{t("rpt.title")}</h1>
          <p className="mt-1 text-sm text-brand-grey">{t("rpt.subtitle")}</p>
        </div>
      </div>
      <div className="mt-4 grid gap-4 lg:grid-cols-3">
        <div className="space-y-4 lg:col-span-2">
          <Card className="p-4">
            <label className="text-[11px] font-semibold uppercase tracking-wide text-brand-grey">{t("rpt.instruction")}</label>
            <textarea value={instruction} onChange={(e) => setInstruction(e.target.value)} rows={2} placeholder={t("rpt.placeholder")} data-report-instruction
              className="mt-1 w-full rounded border border-brand-grey-light px-3 py-2 text-sm focus:border-brand-primary focus:outline-none" />
            <div className="mt-2 flex flex-wrap gap-1">
              {EXAMPLES.map((k) => <button key={k} type="button" onClick={() => setInstruction(t(`rpt.examples.${k}`))} className="rounded-full border border-brand-grey-light px-2.5 py-0.5 text-[11px] text-brand-grey hover:border-brand-primary hover:text-brand-primary">{t(`rpt.examples.${k}`)}</button>)}
            </div>
            <div className="mt-3 flex flex-wrap items-center gap-2">
              <button disabled={!!busy || !instruction.trim() || !canAct} onClick={() => void doDraft("rule")} data-report-draft="rule" className="rounded bg-brand-primary px-3 py-1.5 text-sm font-semibold text-white disabled:opacity-50">{busy === "rule" ? t("rpt.drafting") : t("rpt.draftRule")}</button>
              <button disabled={!!busy || !instruction.trim() || !canAct} onClick={() => void doDraft("ai")} data-report-draft="ai" className="rounded bg-violet-600 px-3 py-1.5 text-sm font-semibold text-white disabled:opacity-50">✦ {busy === "ai" ? t("rpt.drafting") : t("rpt.draftAi")}</button>
              <span className="text-[11px] text-brand-grey">{t("rpt.reviewHint")}</span>
            </div>
            {error && <div className="mt-2"><ErrorBox error={error} /></div>}
          </Card>
          {draft && !spec && (
            <Card className="p-4 text-sm">
              <div className="font-semibold text-status-amber">{t("rpt.notUnderstood")}</div>
              {draft.candidates && draft.candidates.length > 0 && <div className="mt-1 text-xs text-brand-grey">{draft.candidates.join(" · ")}</div>}
            </Card>
          )}
          {spec && (
            <Card className="p-4" data-report-spec>
              <div className="flex flex-wrap items-center justify-between gap-2">
                <h2 className="flex items-center gap-2 font-heading text-sm font-semibold">{t("rpt.spec")} <ProvenanceBadge source={draft!.source === "ai" ? "ai" : draft!.source === "human" ? "human" : "rule"} />{draft!.model && <span className="text-[11px] font-normal text-brand-grey">{draft!.model}</span>}</h2>
                <div className="flex flex-wrap items-center gap-2 text-xs">
                  <span className="text-brand-grey">{t("rpt.chart")}</span>
                  {(["table", "bar", "line"] as const).map((c) => <button key={c} type="button" onClick={() => setChart(c)} disabled={c !== "table" && !spec.measure} className={`rounded border px-2 py-0.5 ${spec.chart === c ? "border-brand-primary bg-blue-50 text-brand-primary" : "border-brand-grey-light"} disabled:opacity-40`}>{t(`rpt.chartTypes.${c}`)}</button>)}
                  <button disabled={busy === "run"} onClick={() => void doRun(spec)} data-report-run className="rounded bg-brand-primary px-3 py-1 font-semibold text-white disabled:opacity-50">{busy === "run" ? t("rpt.running") : t("rpt.run")}</button>
                </div>
              </div>
              {draft!.explanation && <p className="mt-2 text-sm text-brand-charcoal">{draft!.explanation}</p>}
              <div className="mt-2 flex flex-wrap gap-1.5 text-[11px]">
                <Chip label={t("rpt.recordType")} value={spec.doctype} />
                {spec.measure && <Chip label={t("rpt.measure")} value={`${t(`rpt.agg.${spec.measure.agg}`)}${spec.measure.field ? ` · ${run?.labels?.[spec.measure.field] ?? spec.measure.field}` : ""}`} />}
                {spec.group_by && <Chip label={t("rpt.groupBy")} value={run?.labels?.[spec.group_by] ?? spec.group_by} />}
                {spec.bucket && <Chip label={t("rpt.bucket")} value={t(`rpt.buckets.${spec.bucket}`)} />}
                {(spec.filters ?? []).map((f, i) => <Chip key={i} label={t("rpt.filter")} value={`${run?.labels?.[String(f[0])] ?? f[0]} ${f[1]} ${Array.isArray(f[2]) ? f[2].join(" → ") : String(f[2])}`} />)}
                <Chip label={t("rpt.limit")} value={String(spec.limit)} />
              </div>
              {(draft!.errors.length > 0 || draft!.notes.length > 0) && (
                <ul className="mt-2 list-disc pl-5 text-[11px] text-brand-grey">
                  {draft!.errors.map((e, i) => <li key={`e${i}`} className="text-status-amber">{t("rpt.dropped")}: {e}</li>)}
                  {draft!.notes.map((n, i) => <li key={`n${i}`}>{n}</li>)}
                </ul>
              )}
              {draft!.source === "ai" && <FeedbackControl interactionId={draft!.interaction_id} size="xxs" className="mt-2" />}
            </Card>
          )}
          {run && spec && (
            <Card className="p-4" data-report-result>
              <div className="flex flex-wrap items-center justify-between gap-2 text-xs text-brand-grey">
                <h2 className="font-heading text-sm font-semibold text-brand-charcoal">{t("rpt.results")}</h2>
                <span>{fmtNumber(run.result.count, lng, 0)} {t("rpt.rows")}{run.result.groups != null ? ` · ${fmtNumber(run.result.groups, lng, 0)} ${t("rpt.groups")}` : ""}{run.result.total != null ? ` · ${t("rpt.total")} ${fmtNumber(run.result.total, lng, 2)}` : ""}{run.result.truncated ? ` · ${t("rpt.truncated")}` : ""}</span>
              </div>
              {run.result.rows.length === 0 ? <p className="mt-2 text-sm text-brand-grey">{t("rpt.noRows")}</p> : run.result.mode === "aggregate" ? (
                <>
                  {spec.chart !== "table" && <MiniChart rows={run.result.rows as { group: string; value: number }[]} kind={spec.chart} lng={lng} />}
                  <table className="mt-2 w-full text-xs">
                    <thead className="text-left text-[11px] uppercase tracking-wide text-brand-grey"><tr><th className="py-1">{run.labels?.[spec.group_by ?? ""] ?? (spec.bucket ? t(`rpt.buckets.${spec.bucket}`) : t("rpt.groupBy"))}</th><th className="text-right">{spec.measure ? t(`rpt.agg.${spec.measure.agg}`) : ""}</th><th className="text-right">{t("rpt.count")}</th></tr></thead>
                    <tbody>{(run.result.rows as { group: string; value: number; count: number }[]).map((r) => <tr key={r.group} className="border-t border-brand-grey-light/70"><td className="py-1">{r.group}</td><td className="text-right tabular-nums font-semibold">{fmtNumber(r.value, lng, 2)}</td><td className="text-right tabular-nums text-brand-grey">{r.count}</td></tr>)}</tbody>
                  </table>
                </>
              ) : (
                <div className="mt-2 overflow-x-auto">
                  <table className="w-full text-xs">
                    <thead className="text-left text-[11px] uppercase tracking-wide text-brand-grey"><tr>{run.result.columns.map((c) => <th key={c} className="py-1 pr-3">{run.labels?.[c] ?? c}</th>)}</tr></thead>
                    <tbody>{(run.result.rows as Record<string, unknown>[]).map((r, i) => <tr key={i} className="border-t border-brand-grey-light/70">{run.result.columns.map((c) => <td key={c} className="py-1 pr-3 whitespace-nowrap">{typeof r[c] === "number" ? fmtNumber(r[c] as number, lng, 2) : String(r[c] ?? "")}</td>)}</tr>)}</tbody>
                  </table>
                </div>
              )}
              <div className="mt-3 flex flex-wrap items-center gap-2 border-t border-brand-grey-light pt-3 text-xs">
                <input value={title} onChange={(e) => setTitle(e.target.value)} placeholder={t("rpt.titlePlaceholder")} className="min-w-[16rem] flex-1 rounded border border-brand-grey-light px-2 py-1" data-report-title />
                <label className="flex items-center gap-1 text-brand-grey"><input type="checkbox" checked={shared} onChange={(e) => setShared(e.target.checked)} /> {t("rpt.shareWithRoles")}</label>
                <button disabled={busy === "save"} onClick={() => void doSave()} data-report-save className="rounded border border-brand-primary px-3 py-1 font-semibold text-brand-primary disabled:opacity-50">{busy === "save" ? t("rpt.saving") : t("rpt.save")}</button>
                {savedName && <span className="font-semibold text-status-green">{t("rpt.saved")} · {savedName}</span>}
              </div>
            </Card>
          )}
        </div>
        <Card className="p-4" data-report-saved>
          <h2 className="font-heading text-sm font-semibold">{t("rpt.savedTitle")} {saved.data && <span className="text-brand-grey">· {saved.data.count}</span>}</h2>
          {saved.isLoading && <Skeleton className="mt-2 h-20" />}
          {saved.isError && <div className="mt-2"><ErrorBox error={saved.error} onRetry={() => void saved.refetch()} /></div>}
          {saved.data && saved.data.items.length === 0 && <p className="mt-2 text-xs text-brand-grey">{t("rpt.noSaved")}</p>}
          <ul className="mt-2 space-y-1 text-xs">
            {saved.data?.items.map((s) => (
              <li key={s.name} className="rounded border border-brand-grey-light/70 px-2 py-1.5">
                <div className="flex flex-wrap items-center justify-between gap-1">
                  <button onClick={() => void openSaved(s.name, s.spec)} className="text-left font-medium text-brand-primary hover:underline">{s.pinned ? "★ " : ""}{s.title}</button>
                  <ProvenanceBadge source={s.source === "ai" ? "ai" : s.source === "human" ? "human" : "rule"} />
                </div>
                <div className="mt-0.5 flex flex-wrap items-center justify-between gap-1 text-[11px] text-brand-grey">
                  <span>{s.doctype_name} · {s.is_shared ? t("rpt.shared") : t("rpt.mine")} · {s.run_count ?? 0} {t("rpt.runCount")}</span>
                  {s.mine && <span className="flex gap-1">
                    <button onClick={async () => { await updateSavedReport(s.name, { pinned: !s.pinned }); void saved.refetch(); }} className="hover:text-brand-primary">{s.pinned ? t("rpt.unpin") : t("rpt.pin")}</button>
                    <button onClick={async () => { await updateSavedReport(s.name, { is_shared: !s.is_shared }); void saved.refetch(); }} className="hover:text-brand-primary">{s.is_shared ? t("rpt.unshare") : t("rpt.share")}</button>
                    <button onClick={async () => { await updateSavedReport(s.name, { delete: true }); void saved.refetch(); }} className="hover:text-status-red">{t("rpt.delete")}</button>
                  </span>}
                </div>
              </li>
            ))}
          </ul>
        </Card>
      </div>
    </AppShell>
  );
}

function Chip({ label, value }: { label: string; value: string }) {
  return <span className="rounded border border-brand-grey-light bg-[#F6F8FA] px-2 py-0.5"><span className="text-brand-grey">{label}:</span> <span className="font-medium">{value}</span></span>;
}

/** Bars or a line over the aggregated rows — deterministic SVG, no library. */
function MiniChart({ rows, kind, lng }: { rows: { group: string; value: number }[]; kind: "bar" | "line" | "table"; lng: string }) {
  const data = kind === "line" ? [...rows].sort((a, b) => a.group.localeCompare(b.group)) : rows.slice(0, 24);
  const max = Math.max(...data.map((r) => Math.abs(r.value)), 1);
  const w = 720, h = 180, pad = 24, bw = (w - pad * 2) / Math.max(data.length, 1);
  const y = (v: number) => h - pad - (Math.abs(v) / max) * (h - pad * 2);
  return (
    <svg viewBox={`0 0 ${w} ${h}`} className="mt-2 w-full" role="img" aria-label={kind} data-report-chart={kind}>
      <line x1={pad} y1={h - pad} x2={w - pad} y2={h - pad} stroke="#D6DBE1" />
      {kind === "bar" ? data.map((r, i) => (
        <g key={r.group}>
          <rect x={pad + i * bw + bw * 0.15} y={y(r.value)} width={bw * 0.7} height={h - pad - y(r.value)} fill="#0B4F9C" opacity={0.85} />
          <text x={pad + i * bw + bw / 2} y={h - pad + 12} fontSize={9} textAnchor="middle" fill="#6B7280">{r.group.length > 12 ? `${r.group.slice(0, 11)}…` : r.group}</text>
          <text x={pad + i * bw + bw / 2} y={y(r.value) - 3} fontSize={9} textAnchor="middle" fill="#111827">{fmtNumber(r.value, lng, 0)}</text>
        </g>
      )) : (
        <>
          <polyline fill="none" stroke="#0B4F9C" strokeWidth={2} points={data.map((r, i) => `${pad + i * bw + bw / 2},${y(r.value)}`).join(" ")} />
          {data.map((r, i) => <g key={r.group}><circle cx={pad + i * bw + bw / 2} cy={y(r.value)} r={3} fill="#0B4F9C" /><text x={pad + i * bw + bw / 2} y={h - pad + 12} fontSize={9} textAnchor="middle" fill="#6B7280">{r.group}</text><text x={pad + i * bw + bw / 2} y={y(r.value) - 6} fontSize={9} textAnchor="middle" fill="#111827">{fmtNumber(r.value, lng, 0)}</text></g>)}
        </>
      )}
    </svg>
  );
}
